import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import CreateSectorCity from "./CreateSectorCity";
import {
  ActiveInactiveSectorCity,
  deleteSectorCity,
  getAllSectorCities,
  getSectorCityByID,
} from "./services/sectorCity.services";
import { clearSectorCityID, setSectorCityID } from "./sectorCitySlice";

const SectorCity: React.FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getAllSectorCities());
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleCreate = () => {
    dispatch(openDialog("sectorCityForm"));
    dispatch(setSectorCityID(0));
  };

  return (
    <>
      <CreateSectorCity />
      <DeleteSectorCityDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Sector City List
          </Typography.h4>
          <Button
            onClick={handleCreate}
            fillMode={"solid"}
            themeColor={"primary"}
          >
            {"Create Sector City"}
          </Button>
        </GridLayoutItem>
        <GridLayoutItem>
          <SectorCityGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const SectorCityGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const SectorCityList = useAppSelector(
    (state: RootState) => state.sectorCity.SectorCityList,
  );
  const loading = useAppSelector(
    (state: RootState) => state.sectorCity.loading,
  );

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? SectorCityList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (ID: number) => {
      dispatch(openDialog("sectorCityForm"));
      dispatch(getSectorCityByID(ID));
      dispatch(setSectorCityID(ID));
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteSectorCity"));
      dispatch(setSectorCityID(ID));
    };

    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Edit"
          onClick={() => handleEdit(props.dataItem.ID)}
          icon={FiEdit}
          iconClassName="absolute-position"
          iconStyle={{ fontSize: "20px" }}
        />

        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Delete"
          className="color-red"
          onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          icon={MdDelete}
          iconClassName="absolute-position"
          iconStyle={{ fontSize: "24px" }}
        />
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveSectorCity(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllSectorCities());
        } else {
          dispatch(getAllSectorCities());
        }
      });
    };

    return (
      <td style={{ textAlign: "center" }}>
        <Switch
          name="IsActive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {SectorCityList && SectorCityList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(SectorCityList, filter).slice(
                  page.skip,
                  page.take + page.skip,
                )
              : SectorCityList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={SectorCityList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column field="SectorName" title="Sector" />
          <Column field="CityName" title="City" />
          <Column
            field="ID"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          <Column
            field="ID"
            title="Actions"
            cell={MyEditCommandCell}
            width={"110px"}
            filterable={false}
          />
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteSectorCityDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const SectorCityID = useAppSelector(
    (state: RootState) => state.sectorCity.SectorCityID,
  );
  const dialogName = useAppSelector(
    (state: RootState) => state.dialog.dialogName,
  );
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteSectorCity(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getAllSectorCities());
        dispatch(closeDialog());
        dispatch(clearSectorCityID());
      } else {
        dispatch(getAllSectorCities());
        dispatch(closeDialog());
        dispatch(clearSectorCityID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteSectorCity" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Sector City"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Sector City?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(SectorCityID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default SectorCity;
